import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, CheckCircle, XCircle, Circle, Play, ArrowLeft, Pause, SkipForward, SkipBack, Maximize, Volume2, Award, TrendingUp, Plus, Check } from 'lucide-react';
import { ArchiveQuestionModal } from '../components/ArchiveQuestionModal';
// Answer key definitions - updated to 4 options (A,B,C,D)
const ANSWER_KEYS = {
  turkce: ['A', 'B', 'C', 'D', 'A', 'B', 'C', 'D', 'A', 'B', 'C', 'D', 'A', 'B', 'C', 'D', 'A', 'B', 'C', 'D'],
  inkılap: ['B', 'C', 'A', 'D', 'B', 'C', 'A', 'D', 'B', 'C'],
  din: ['C', 'A', 'B', 'D', 'C', 'A', 'B', 'D', 'C', 'A'],
  ingilizce: ['D', 'B', 'A', 'C', 'D', 'B', 'A', 'C', 'D', 'B'],
  matematik: ['A', 'C', 'B', 'D', 'A', 'C', 'B', 'D', 'A', 'C', 'B', 'D', 'A', 'C', 'B', 'D', 'A', 'C', 'B', 'D'],
  fen: ['B', 'D', 'A', 'C', 'B', 'D', 'A', 'C', 'B', 'D', 'A', 'C', 'B', 'D', 'A', 'C', 'B', 'D', 'A', 'C']
};
// Interface for student answer data
interface AnswerData {
  soru_no: number;
  ders: string;
  ogrenci_cevabi: string | null;
  dogru_cevap: string;
}
// Result data types
interface QuestionResult {
  id: number;
  subject: string;
  topic: string;
  text: string;
  image?: string;
  options: {
    id: string;
    text: string;
  }[];
  selectedOption?: string | null;
  correctOption: string;
  explanation: string;
  videoUrl: string;
}
interface SubjectResult {
  id: string;
  name: string;
  questionCount: number;
  correct: number;
  incorrect: number;
  empty: number;
  net: number;
  standardScore?: number; // SP score
}
interface ExamResult {
  id: number;
  title: string;
  date: string;
  totalCorrect: number;
  totalIncorrect: number;
  totalEmpty: number;
  totalNet: number;
  subjects: SubjectResult[];
  questions: QuestionResult[];
  lgsPuan?: number; // MSP score
  siralamaTahmini?: number; // Approximate ranking
}
// Subject configuration with question counts and averages/standard deviations for SP calculation
const subjectConfig = [{
  id: 'turkce',
  name: 'Türkçe',
  questionCount: 20,
  average: 12,
  stdDev: 3,
  weight: 4 // Weight for ASP calculation
}, {
  id: 'inkılap',
  name: 'T.C. İnkılap Tarihi ve Atatürkçülük',
  questionCount: 10,
  average: 6,
  stdDev: 2,
  weight: 1 // Weight for ASP calculation
}, {
  id: 'din',
  name: 'Din Kültürü ve Ahlak Bilgisi',
  questionCount: 10,
  average: 7,
  stdDev: 2,
  weight: 1 // Weight for ASP calculation
}, {
  id: 'ingilizce',
  name: 'İngilizce',
  questionCount: 10,
  average: 5,
  stdDev: 2,
  weight: 1 // Weight for ASP calculation
}, {
  id: 'matematik',
  name: 'Matematik',
  questionCount: 20,
  average: 10,
  stdDev: 4,
  weight: 4 // Weight for ASP calculation
}, {
  id: 'fen',
  name: 'Fen Bilimleri',
  questionCount: 20,
  average: 11,
  stdDev: 3.5,
  weight: 4 // Weight for ASP calculation
}];
// Constants for MSP calculation
const ASP_MIN = 200;
const ASP_MAX = 800;
// Video URLs for solutions
const videoUrls = ['https://www.youtube.com/embed/dQw4w9WgXcQ', 'https://www.youtube.com/embed/jNQXAC9IVRw', 'https://www.youtube.com/embed/8ybW48rKBME', 'https://www.youtube.com/embed/FJ_s3RQg0BI', 'https://www.youtube.com/embed/9bZkp7q19f0'];
// Function to estimate ranking based on MSP score
const estimateRanking = (mspScore: number): number => {
  // This is a simplified estimation model
  if (mspScore >= 490) return Math.round(1000 + (500 - mspScore) * 40);
  if (mspScore >= 450) return Math.round(10000 + (490 - mspScore) * 800);
  if (mspScore >= 400) return Math.round(50000 + (450 - mspScore) * 1500);
  if (mspScore >= 350) return Math.round(125000 + (400 - mspScore) * 2500);
  if (mspScore >= 300) return Math.round(250000 + (350 - mspScore) * 3500);
  return Math.round(425000 + (300 - mspScore) * 5000);
};
// Function to calculate real exam results from detailed answer data
const calculateExamResults = (detailedAnswers: AnswerData[], examId: number, examTitle: string): ExamResult => {
  // Debugging: log all answers received for validation
  console.log('All detailed answers received:', detailedAnswers);
  // Group answers by subject for easier processing
  const answersBySubject: Record<string, AnswerData[]> = {};
  subjectConfig.forEach(subject => {
    answersBySubject[subject.id] = detailedAnswers.filter(a => a.ders === subject.id);
  });
  // Calculate subject results with the formula: Net = Correct - (Incorrect/4) - updated for 4 options
  const subjectResults: SubjectResult[] = subjectConfig.map(subject => {
    // Get answers for this subject
    const subjectAnswers = answersBySubject[subject.id] || [];
    // Count correct, incorrect, and empty answers
    let correct = 0;
    let incorrect = 0;
    let empty = 0;
    subjectAnswers.forEach(answer => {
      if (answer.ogrenci_cevabi === null) {
        empty++;
      } else if (answer.ogrenci_cevabi === answer.dogru_cevap) {
        correct++;
      } else {
        incorrect++;
      }
    });
    // Calculate net score using the formula: Correct - (Incorrect/4) - updated for 4 options
    const net = +(correct - incorrect / 4).toFixed(2);
    // Calculate standard score (SP) using the formula: 10 × ((Net - Average)/Standard_Deviation) + 50
    const standardScore = +(10 * ((net - subject.average) / subject.stdDev) + 50).toFixed(2);
    // Debug logging for each subject calculation
    console.log(`${subject.name} calculation:`, {
      answers: subjectAnswers.length,
      correct,
      incorrect,
      empty,
      net,
      standardScore
    });
    return {
      id: subject.id,
      name: subject.name,
      questionCount: subject.questionCount,
      correct,
      incorrect,
      empty,
      net,
      standardScore
    };
  });
  // Calculate totals
  const totalCorrect = subjectResults.reduce((sum, subject) => sum + subject.correct, 0);
  const totalIncorrect = subjectResults.reduce((sum, subject) => sum + subject.incorrect, 0);
  const totalEmpty = subjectResults.reduce((sum, subject) => sum + subject.empty, 0);
  const totalNet = +subjectResults.reduce((sum, subject) => sum + subject.net, 0).toFixed(2);
  // Calculate Weighted Score (ASP) using the formula:
  // ASP = (Türkçe_SP×4) + (Matematik_SP×4) + (Fen_SP×4) + (Tarih_SP×1) + (Din_SP×1) + (İngilizce_SP×1)
  let weightedScore = 0;
  subjectResults.forEach(subject => {
    const config = subjectConfig.find(s => s.id === subject.id);
    if (config && subject.standardScore) {
      weightedScore += subject.standardScore * config.weight;
    }
  });
  // Calculate final LGS score (MSP) using the formula:
  // MSP = 100 + ((ASP - ASP_min) × 400) / (ASP_max - ASP_min)
  const lgsPuan = Math.round(100 + (weightedScore - ASP_MIN) * 400 / (ASP_MAX - ASP_MIN));
  // Estimate ranking based on MSP score
  const siralamaTahmini = estimateRanking(lgsPuan);
  // Create questions array with real data
  const questions: QuestionResult[] = [];
  // Process each answer to create question results
  detailedAnswers.forEach(answer => {
    // Find the subject this question belongs to
    const subject = subjectResults.find(s => s.id === answer.ders);
    if (!subject) return;
    // Create a unique ID for the question (based on subject and question number)
    let globalQuestionId = answer.soru_no;
    // Adjust global question ID based on subject - this creates a unique ID for each question across subjects
    if (answer.ders === 'inkılap') {
      globalQuestionId = answer.soru_no + 20; // 21-30
    } else if (answer.ders === 'din') {
      globalQuestionId = answer.soru_no + 30; // 31-40
    } else if (answer.ders === 'ingilizce') {
      globalQuestionId = answer.soru_no + 40; // 41-50
    } else if (answer.ders === 'matematik') {
      globalQuestionId = answer.soru_no + 50; // 51-70
    } else if (answer.ders === 'fen') {
      globalQuestionId = answer.soru_no + 70; // 71-90
    }
    questions.push({
      id: globalQuestionId,
      subject: answer.ders,
      topic: `${subject.name} Konu ${Math.floor(answer.soru_no % subject.questionCount / 3) + 1}`,
      text: `${subject.name} soru ${answer.soru_no}: Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam auctor, nisl eget ultricies aliquam, nunc nisl aliquet nunc, quis aliquam nisl nunc quis nisl.`,
      image: globalQuestionId % 5 === 0 ? `https://picsum.photos/seed/${answer.ders}${answer.soru_no}/800/400` : undefined,
      options: [{
        id: 'A',
        text: 'Lorem ipsum dolor sit amet'
      }, {
        id: 'B',
        text: 'Consectetur adipiscing elit'
      }, {
        id: 'C',
        text: 'Nullam auctor, nisl eget ultricies aliquam'
      }, {
        id: 'D',
        text: 'Nunc nisl aliquet nunc, quis aliquam nisl nunc quis nisl'
      }],
      selectedOption: answer.ogrenci_cevabi,
      correctOption: answer.dogru_cevap,
      explanation: 'Bu sorunun çözümü için öncelikle verilen metni dikkatlice okumanız ve ana fikri bulmanız gerekiyor. Ardından, seçeneklerden hangisinin ana fikre en yakın olduğunu tespit etmelisiniz.',
      videoUrl: videoUrls[Math.floor(Math.random() * videoUrls.length)]
    });
  });
  // Sort questions by ID to maintain proper order
  questions.sort((a, b) => a.id - b.id);
  // Log calculation results for debugging
  console.log('Final calculation results:', {
    subjectResults,
    totalCorrect,
    totalIncorrect,
    totalEmpty,
    totalNet,
    lgsPuan,
    questionCount: questions.length
  });
  // Create the complete result object with all calculated values
  return {
    id: examId,
    title: examTitle,
    date: new Date().toISOString().split('T')[0],
    totalCorrect,
    totalIncorrect,
    totalEmpty,
    totalNet,
    subjects: subjectResults,
    questions,
    lgsPuan,
    siralamaTahmini
  };
};
export function ExamResults() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedSubject, setSelectedSubject] = useState<string>('turkce');
  const [selectedResultType, setSelectedResultType] = useState<string>('all');
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
  const [showResultTypeDropdown, setShowResultTypeDropdown] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState<number[]>([]);
  const [examResult, setExamResult] = useState<ExamResult | null>(null);
  const [loading, setLoading] = useState(true);
  // State for archive modal
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [questionToArchive, setQuestionToArchive] = useState<QuestionResult | null>(null);
  const [archivedQuestions, setArchivedQuestions] = useState<number[]>([]);
  // Handle archive question
  const handleArchiveQuestion = (data: {
    difficulty: 'Kolay' | 'Orta' | 'Zor';
    notes: string;
  }) => {
    if (!questionToArchive) return;
    // Get all questions from localStorage
    const savedQuestions = localStorage.getItem('questions');
    let allQuestions = savedQuestions ? JSON.parse(savedQuestions) : [];
    // Create a new archived question
    const archivedQuestion = {
      id: questionToArchive.id,
      subject: questionToArchive.subject,
      topic: questionToArchive.topic,
      subtopic: questionToArchive.text.substring(0, 60) + (questionToArchive.text.length > 60 ? '...' : ''),
      difficulty: data.difficulty,
      solved: questionToArchive.selectedOption === questionToArchive.correctOption,
      date: new Date().toISOString(),
      archived: true,
      content: questionToArchive.text,
      options: questionToArchive.options.map(o => o.text),
      correctOption: questionToArchive.options.findIndex(o => o.id === questionToArchive.correctOption),
      image: questionToArchive.image,
      likes: [],
      views: 0,
      notes: data.notes,
      videoUrl: questionToArchive.videoUrl,
      explanation: questionToArchive.explanation
    };
    // Check if question already exists
    const existingIndex = allQuestions.findIndex((q: any) => q.id === questionToArchive.id);
    if (existingIndex >= 0) {
      // Update existing
      allQuestions[existingIndex] = archivedQuestion;
    } else {
      // Add new
      allQuestions.push(archivedQuestion);
    }
    // Save to localStorage
    localStorage.setItem('questions', JSON.stringify(allQuestions));
    // Update local state
    setArchivedQuestions([...archivedQuestions, questionToArchive.id]);
    // Close modal
    setShowArchiveModal(false);
    setQuestionToArchive(null);
    // Show success message (optional - you can use a toast)
    alert('Soru başarıyla arşive eklendi!');
  };
  // Check if question is already archived
  useEffect(() => {
    const savedQuestions = localStorage.getItem('questions');
    if (savedQuestions) {
      const allQuestions = JSON.parse(savedQuestions);
      const archived = allQuestions.filter((q: any) => q.archived).map((q: any) => q.id);
      setArchivedQuestions(archived);
    }
  }, []);
  // Calculate results on component mount
  useEffect(() => {
    setLoading(true);
    // Check if we're viewing from storage or calculating new results
    if (location.state?.fromStorage) {
      // Get the completed exam from localStorage
      const storedExams = localStorage.getItem('completedExams');
      if (storedExams) {
        const completedExams = JSON.parse(storedExams);
        const savedExam = completedExams.find((exam: ExamResult) => exam.id === Number(id));
        if (savedExam) {
          setExamResult(savedExam);
          setLoading(false);
          return;
        }
      }
    }
    // If we're not viewing from storage or the exam wasn't found, calculate new results
    // Get detailed answer data from location state
    const detailedAnswers = location.state?.detailedAnswers || [];
    // Log the incoming answer data for debugging
    console.log('Received detailed answers:', detailedAnswers);
    console.log('Verbal answers:', detailedAnswers.filter((a: AnswerData) => a.ders === 'turkce' || a.ders === 'inkılap' || a.ders === 'din' || a.ders === 'ingilizce'));
    console.log('Numerical answers:', detailedAnswers.filter((a: AnswerData) => a.ders === 'matematik' || a.ders === 'fen'));
    // In a real app, we'd fetch the correct answers from the server
    // For now, simulate a delay to fetch data
    const timer = setTimeout(() => {
      const examTitle = `LGS Genel Deneme Sınavı - ${id}`;
      // Calculate real results from detailed answer data
      const calculatedResults = calculateExamResults(detailedAnswers, Number(id), examTitle);
      setExamResult(calculatedResults);
      // Save the completed exam to local storage for persistence
      if (!location.state?.fromStorage && detailedAnswers.length > 0) {
        // Get existing completed exams from local storage
        const storedExams = localStorage.getItem('completedExams');
        const completedExams = storedExams ? JSON.parse(storedExams) : [];
        // Check if this exam is already in the completed exams
        const existingExamIndex = completedExams.findIndex((exam: ExamResult) => exam.id === Number(id));
        // Only add if it doesn't already exist
        if (existingExamIndex === -1) {
          // Add the new exam result to the list
          completedExams.unshift(calculatedResults);
          // Save back to local storage
          localStorage.setItem('completedExams', JSON.stringify(completedExams));
          // Remove the exam from active exams
          const storedActiveExams = localStorage.getItem('activeExams');
          if (storedActiveExams) {
            const activeExams = JSON.parse(storedActiveExams);
            const updatedActiveExams = activeExams.filter((exam: any) => exam.id !== Number(id));
            localStorage.setItem('activeExams', JSON.stringify(updatedActiveExams));
          }
        }
      }
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [id, location.state]);
  // After the exam is completed and results are calculated, navigate back to the exams list
  const handleReturnToExams = () => {
    navigate('/online-denemeler', {
      state: {
        activeTab: 'results'
      }
    });
  };
  if (loading || !examResult) {
    return <div className="flex items-center justify-center h-64" data-id="element-1721">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500" data-id="element-1722"></div>
      </div>;
  }
  // Filter questions based on selected subject and result type
  const filteredQuestions = examResult.questions.filter(question => {
    // Subject filter
    if (question.subject !== selectedSubject) {
      return false;
    }
    // Result type filter
    if (selectedResultType === 'correct') {
      return question.selectedOption === question.correctOption;
    } else if (selectedResultType === 'incorrect') {
      return question.selectedOption && question.selectedOption !== question.correctOption;
    } else if (selectedResultType === 'empty') {
      return !question.selectedOption;
    }
    // "all" result type - show everything for this subject
    return true;
  });
  // Toggle question expansion
  const toggleQuestion = (questionId: number) => {
    setExpandedQuestions(prev => prev.includes(questionId) ? prev.filter(id => id !== questionId) : [...prev, questionId]);
  };
  // Get status icon
  const getStatusIcon = (question: QuestionResult) => {
    if (!question.selectedOption) {
      return <Circle className="w-5 h-5 text-gray-400" data-id="element-1723" />;
    }
    return question.selectedOption === question.correctOption ? <CheckCircle className="w-5 h-5 text-green-500" data-id="element-1724" /> : <XCircle className="w-5 h-5 text-red-500" data-id="element-1725" />;
  };
  // Get the current subject object
  const currentSubject = examResult.subjects.find(s => s.id === selectedSubject);
  return <div className="space-y-8 pb-12" data-id="element-1726">
      {/* Back button */}
      <button onClick={handleReturnToExams} className="inline-flex items-center gap-2 px-4 py-2 text-white bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg hover:opacity-90 transition-colors" data-id="element-1727">
        <ArrowLeft className="w-4 h-4" data-id="element-1728" />
        Denemelere Geri Dön
      </button>
      <h1 className="text-3xl font-bold" data-id="element-1729">{examResult.title} Sonuçları</h1>
      {/* Summary section with LGS score and ranking */}
      <div className="border border-purple-500 rounded-xl overflow-hidden" data-id="element-1730">
        <div className="p-6" data-id="element-1731">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-1732">
            <div className="text-center md:text-left space-y-2" data-id="element-1733">
              <h2 className="text-2xl font-bold mb-2" data-id="element-1734">LGS Puanı</h2>
              <div className="text-5xl font-bold text-purple-400" data-id="element-1735">
                {examResult.lgsPuan?.toFixed(2)}
              </div>
              <div className="flex items-center gap-2 text-gray-300" data-id="element-1736">
                <TrendingUp className="w-4 h-4 text-yellow-500" data-id="element-1737" />
                <span data-id="element-1738">
                  Tahmini Sıralama:{' '}
                  <span className="font-semibold" data-id="element-1739">
                    {examResult.siralamaTahmini?.toLocaleString()}
                  </span>
                </span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-6" data-id="element-1740">
              <div className="text-center" data-id="element-1741">
                <div className="text-3xl font-bold text-green-400" data-id="element-1742">
                  {examResult.totalCorrect}
                </div>
                <p className="text-gray-400" data-id="element-1743">Doğru</p>
              </div>
              <div className="text-center" data-id="element-1744">
                <div className="text-3xl font-bold text-red-400" data-id="element-1745">
                  {examResult.totalIncorrect}
                </div>
                <p className="text-gray-400" data-id="element-1746">Yanlış</p>
              </div>
              <div className="text-center" data-id="element-1747">
                <div className="text-3xl font-bold text-blue-400" data-id="element-1748">
                  {examResult.totalEmpty}
                </div>
                <p className="text-gray-400" data-id="element-1749">Boş</p>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-800" data-id="element-1750">
            <div className="flex justify-between items-center" data-id="element-1751">
              <span className="text-gray-400" data-id="element-1752">Toplam Net:</span>
              <span className="font-bold text-purple-400" data-id="element-1753">
                {examResult.totalNet.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Subject-based results cards with standard scores */}
      <div className="space-y-4" data-id="element-1754">
        <h3 className="text-xl font-bold" data-id="element-1755">Ders Bazlı Sonuçlar</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1756">
          {examResult.subjects.map(subject => <div key={subject.id} className="border border-purple-500 rounded-xl overflow-hidden" data-id="element-1757">
              <div className="p-4" data-id="element-1758">
                <h4 className="text-lg font-bold mb-3" data-id="element-1759">{subject.name}</h4>
                <div className="space-y-2" data-id="element-1760">
                  <div className="flex justify-between items-center" data-id="element-1761">
                    <span className="text-green-400 font-medium" data-id="element-1762">Doğru:</span>
                    <span className="text-green-400 font-bold" data-id="element-1763">
                      {subject.correct}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-1764">
                    <span className="text-red-400 font-medium" data-id="element-1765">Yanlış:</span>
                    <span className="text-red-400 font-bold" data-id="element-1766">
                      {subject.incorrect}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-1767">
                    <span className="text-blue-400 font-medium" data-id="element-1768">Boş:</span>
                    <span className="text-blue-400 font-bold" data-id="element-1769">
                      {subject.empty}
                    </span>
                  </div>
                  <div className="pt-2 mt-2 border-t border-gray-800" data-id="element-1770">
                    <div className="flex justify-between items-center" data-id="element-1771">
                      <span className="text-purple-400 font-medium" data-id="element-1772">Net:</span>
                      <span className="text-purple-400 font-bold" data-id="element-1773">
                        {subject.net.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <div className="pt-2 mt-2 border-t border-gray-800" data-id="element-1774">
                    <div className="flex justify-between items-center" data-id="element-1775">
                      <span className="text-yellow-400 font-medium" data-id="element-1776">
                        Standart Puan:
                      </span>
                      <span className="text-yellow-400 font-bold" data-id="element-1777">
                        {subject.standardScore?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
      {/* Question review section */}
      <div className="space-y-6" data-id="element-1778">
        <h2 className="text-2xl font-bold" data-id="element-1779">Soru İncelemesi</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1780">
          {/* Subject dropdown with question counts */}
          <div className="relative" data-id="element-1781">
            <div className="mb-2 text-gray-400 text-sm" data-id="element-1782">Ders Seçimi</div>
            <button onClick={() => setShowSubjectDropdown(!showSubjectDropdown)} className="w-full flex items-center justify-between gap-2 px-6 py-3 bg-gradient-to-r from-purple-600/20 to-indigo-600/20 border border-purple-500/30 rounded-lg hover:border-purple-500/50 transition-colors" data-id="element-1783">
              <span className="text-white font-medium text-lg" data-id="element-1784">
                {currentSubject ? `${currentSubject.name} (${currentSubject.questionCount} soru)` : 'Ders Seçin'}
              </span>
              <ChevronDown className={`w-5 h-5 text-purple-400 transition-transform ${showSubjectDropdown ? 'rotate-180' : ''}`} data-id="element-1785" />
            </button>
            {showSubjectDropdown && <div className="absolute top-full left-0 mt-1 z-10 w-full bg-gray-900/95 border border-gray-700 rounded-lg shadow-lg py-1 max-h-60 overflow-y-auto" data-id="element-1786">
                {examResult.subjects.map(subject => <button key={subject.id} onClick={() => {
              setSelectedSubject(subject.id);
              setShowSubjectDropdown(false);
            }} className="w-full text-left px-4 py-3 hover:bg-purple-500/20 transition-colors" data-id="element-1787">
                    {subject.name} ({subject.questionCount} soru)
                  </button>)}
              </div>}
          </div>
          {/* Result type dropdown */}
          <div className="relative" data-id="element-1788">
            <div className="mb-2 text-gray-400 text-sm" data-id="element-1789">Sonuç Filtresi</div>
            <button onClick={() => setShowResultTypeDropdown(!showResultTypeDropdown)} className="w-full flex items-center justify-between gap-2 px-6 py-3 bg-gradient-to-r from-purple-600/20 to-indigo-600/20 border border-purple-500/30 rounded-lg hover:border-purple-500/50 transition-colors" data-id="element-1790">
              <span className="text-white font-medium text-lg" data-id="element-1791">
                {selectedResultType === 'all' && 'Tüm Sorular'}
                {selectedResultType === 'correct' && 'Sadece Doğrular'}
                {selectedResultType === 'incorrect' && 'Sadece Yanlışlar'}
                {selectedResultType === 'empty' && 'Sadece Boşlar'}
              </span>
              <ChevronDown className={`w-5 h-5 text-purple-400 transition-transform ${showResultTypeDropdown ? 'rotate-180' : ''}`} data-id="element-1792" />
            </button>
            {showResultTypeDropdown && <div className="absolute top-full left-0 mt-1 z-10 w-full bg-gray-900/95 border border-gray-700 rounded-lg shadow-lg py-1 max-h-60 overflow-y-auto" data-id="element-1793">
                <button onClick={() => {
              setSelectedResultType('all');
              setShowResultTypeDropdown(false);
            }} className="w-full text-left px-4 py-3 hover:bg-purple-500/20 transition-colors" data-id="element-1794">
                  Tüm Sorular
                </button>
                <button onClick={() => {
              setSelectedResultType('correct');
              setShowResultTypeDropdown(false);
            }} className="w-full text-left px-4 py-3 hover:bg-purple-500/20 transition-colors" data-id="element-1795">
                  Sadece Doğru
                </button>
                <button onClick={() => {
              setSelectedResultType('incorrect');
              setShowResultTypeDropdown(false);
            }} className="w-full text-left px-4 py-3 hover:bg-purple-500/20 transition-colors" data-id="element-1796">
                  Sadece Yanlış
                </button>
                <button onClick={() => {
              setSelectedResultType('empty');
              setShowResultTypeDropdown(false);
            }} className="w-full text-left px-4 py-3 hover:bg-purple-500/20 transition-colors" data-id="element-1797">
                  Sadece Boş
                </button>
              </div>}
          </div>
        </div>
        {/* Filtered questions count */}
        <div className="text-gray-400 text-sm" data-id="element-1798">
          {filteredQuestions.length} soru listeleniyor
        </div>
        {/* Questions accordion */}
        <div className="space-y-3 mt-6" data-id="element-1799">
          {filteredQuestions.length > 0 ? filteredQuestions.map(question => {
          const isExpanded = expandedQuestions.includes(question.id);
          const isCorrect = question.selectedOption === question.correctOption;
          const isIncorrect = !!question.selectedOption && question.selectedOption !== question.correctOption;
          const isEmpty = !question.selectedOption;
          let borderColor = 'border-blue-500';
          if (isCorrect) borderColor = 'border-green-500';
          if (isIncorrect) borderColor = 'border-red-500';
          return <div key={question.id} className={`border ${borderColor} rounded-xl overflow-hidden`} data-id="element-1800">
                  <div data-id="element-1801">
                    {/* Accordion header */}
                    <button onClick={() => toggleQuestion(question.id)} className="flex items-center justify-between w-full px-6 py-4 text-left" data-id="element-1802">
                      <div className="flex items-center gap-3" data-id="element-1803">
                        {getStatusIcon(question)}
                        <div data-id="element-1804">
                          <span className="font-medium" data-id="element-1805">{question.topic}</span>
                          <p className="text-sm text-gray-400 mt-1" data-id="element-1806">
                            Soru {question.id}: {question.text.substring(0, 50)}
                            ...
                          </p>
                        </div>
                      </div>
                      <ChevronDown className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-1807" />
                    </button>
                    {/* Accordion content */}
                    {isExpanded && <div className="px-6 pb-6 pt-2 border-t border-gray-800" data-id="element-1808">
                        <div className="space-y-4" data-id="element-1809">
                          {/* Question text */}
                          <div className="prose prose-invert max-w-none" data-id="element-1810">
                            <p data-id="element-1811">{question.text}</p>
                          </div>
                          {/* Question image if exists */}
                          {question.image && <div className="mt-4" data-id="element-1812">
                              <img src={question.image} alt="Soru görseli" className="rounded-lg max-w-full max-h-96 mx-auto" data-id="element-1813" />
                            </div>}
                          {/* Options */}
                          <div className="space-y-3 mt-4" data-id="element-1814">
                            {question.options.map(option => {
                      const isSelected = question.selectedOption === option.id;
                      const isCorrect = option.id === question.correctOption;
                      let optionClass = 'bg-white/5 text-gray-300 border border-transparent';
                      if (isSelected && isCorrect) {
                        optionClass = 'bg-green-500/20 text-green-400 border border-green-500';
                      } else if (isSelected && !isCorrect) {
                        optionClass = 'bg-red-500/20 text-red-400 border border-red-500';
                      } else if (!isSelected && isCorrect) {
                        optionClass = 'bg-green-500/20 text-green-400 border border-green-500';
                      }
                      return <div key={option.id} className={`p-4 rounded-lg flex items-center gap-4 ${optionClass}`} data-id="element-1815">
                                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-lg font-medium 
                                      ${isSelected && isCorrect ? 'bg-green-500/20 text-green-400 border border-green-500' : isSelected && !isCorrect ? 'bg-red-500/20 text-red-400 border border-red-500' : !isSelected && isCorrect ? 'bg-green-500/20 text-green-400 border border-green-500' : 'bg-black/60 text-gray-400 border border-gray-600'}`} data-id="element-1816">
                                    {option.id}
                                  </div>
                                  <span className="flex-1" data-id="element-1817">{option.text}</span>
                                  {isSelected && isCorrect && <CheckCircle className="w-5 h-5 text-green-500" data-id="element-1818" />}
                                  {isSelected && !isCorrect && <XCircle className="w-5 h-5 text-red-500" data-id="element-1819" />}
                                  {!isSelected && isCorrect && <CheckCircle className="w-5 h-5 text-green-500" data-id="element-1820" />}
                                </div>;
                    })}
                          </div>
                          {/* Explanation */}
                          <div className="p-4 bg-white/5 rounded-lg text-gray-300 mt-4" data-id="element-1821">
                            <h4 className="font-medium mb-2" data-id="element-1822">Açıklama</h4>
                            <p data-id="element-1823">{question.explanation}</p>
                          </div>
                          {/* Video solution */}
                          <div className="mt-4" data-id="element-1824">
                            <h4 className="font-medium mb-2 flex items-center gap-2" data-id="element-1825">
                              <Play className="w-4 h-4 text-purple-400" data-id="element-1826" />
                              Video Çözüm
                            </h4>
                            <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden" data-id="element-1827">
                              <iframe src={question.videoUrl} className="absolute inset-0 w-full h-full" title="Video Çözüm" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-1828"></iframe>
                              {/* Custom Video Controls (visual only) */}
                              <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-4 py-2 flex items-center justify-between opacity-0 hover:opacity-100 transition-opacity" data-id="element-1829">
                                <div className="flex items-center gap-2" data-id="element-1830">
                                  <button className="text-white p-1 rounded-full hover:bg-white/20" data-id="element-1831">
                                    <Play className="w-4 h-4" data-id="element-1832" />
                                  </button>
                                  <button className="text-white p-1 rounded-full hover:bg-white/20" data-id="element-1833">
                                    <Pause className="w-4 h-4" data-id="element-1834" />
                                  </button>
                                  <button className="text-white p-1 rounded-full hover:bg-white/20" data-id="element-1835">
                                    <SkipBack className="w-4 h-4" data-id="element-1836" />
                                  </button>
                                  <button className="text-white p-1 rounded-full hover:bg-white/20" data-id="element-1837">
                                    <SkipForward className="w-4 h-4" data-id="element-1838" />
                                  </button>
                                  <div className="text-white text-xs ml-2" data-id="element-1839">
                                    00:45 / 05:32
                                  </div>
                                </div>
                                <div className="flex items-center gap-2" data-id="element-1840">
                                  <button className="text-white p-1 rounded-full hover:bg-white/20" data-id="element-1841">
                                    <Volume2 className="w-4 h-4" data-id="element-1842" />
                                  </button>
                                  <button className="text-white p-1 rounded-full hover:bg-white/20" data-id="element-1843">
                                    <Maximize className="w-4 h-4" data-id="element-1844" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                          {/* Archive Button */}
                          <div className="mt-4 pt-4 border-t border-gray-800" data-id="element-1845">
                            <button onClick={e => {
                      e.stopPropagation();
                      setQuestionToArchive(question);
                      setShowArchiveModal(true);
                    }} disabled={archivedQuestions.includes(question.id)} className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${archivedQuestions.includes(question.id) ? 'bg-green-500/20 text-green-400 border border-green-500 cursor-not-allowed' : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:opacity-90'}`} data-id="element-1846">
                              {archivedQuestions.includes(question.id) ? <>
                                  <Check className="w-5 h-5" data-id="element-1847" />
                                  Arşivlendi
                                </> : <>
                                  <Plus className="w-5 h-5" data-id="element-1848" />
                                  Arşive Ekle
                                </>}
                            </button>
                          </div>
                        </div>
                      </div>}
                  </div>
                </div>;
        }) : <div className="text-center py-8" data-id="element-1849">
              <p className="text-gray-400" data-id="element-1850">
                Seçilen filtrelere uygun soru bulunamadı.
              </p>
            </div>}
        </div>
      </div>
      {/* Archive Question Modal */}
      {showArchiveModal && questionToArchive && <ArchiveQuestionModal onClose={() => {
      setShowArchiveModal(false);
      setQuestionToArchive(null);
    }} onSave={handleArchiveQuestion} question={{
      id: questionToArchive.id,
      content: questionToArchive.text
    }} data-id="element-1851" />}
    </div>;
}